import { useEffect, useState } from 'react';
import { CUSTOMER_CONSENT_LABEL } from '@brimo/shared';
import { Banner, Button, Checkbox, Dialog } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { chooseOffer, type FinancierRecordFields, type OfferRow } from './api';
import styles from './financier.module.css';

/**
 * „Wählen" (Finanzierer 06, no frame for the dialog): the offer, the rule of the „Wählen" card and the mandatory
 * consent checkbox (PLAN §5.4 `customer_consented_at`) → `POST …/offers/:offerId/choose { customerConsented: true }`.
 */
export function ChooseOfferDialog({
  financing,
  offer,
  onClose,
  onDone,
}: {
  financing: FinancierRecordFields;
  offer: OfferRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [consented, setConsented] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setConsented(false);
    setFieldError(null);
    setBanner(null);
  }, [offer?.id]);

  async function submit() {
    if (!offer || pending) return;
    if (!consented) {
      setFieldError('Bitte bestätigen Sie die Zustimmung des Kunden.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      await chooseOffer(financing.id, offer.id);
      onDone();
    } catch (error) {
      const field = error instanceof ApiError ? error.fields['customerConsented'] : undefined;
      if (field) setFieldError(field);
      else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={offer !== null}
      onClose={onClose}
      width={560}
      eyebrow={`${financing.numberLabel} · ${financing.linked.contact.name}`}
      title="Angebot wählen"
      subtitle="Sie wählen das Angebot allein. Das gewählte Angebot wird zur Zusage und bestimmt die Stufe."
      footerNote="Stufe wechselt auf „Zusage“"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} disabled={!consented || pending} onClick={() => void submit()}>
            Wählen
          </Button>
        </>
      }
    >
      {offer ? (
        <div className={styles.stack}>
          {banner ? <Banner tone="danger">{banner}</Banner> : null}
          <div className={styles.summary}>
            <div className={styles.summaryBank}>{offer.bank}</div>
            <div className={styles.summaryLine}>
              {offer.line}
              {offer.validLine ? ` · ${offer.validLine}` : ''}
            </div>
          </div>
          <div>
            <Checkbox
              label={CUSTOMER_CONSENT_LABEL}
              checked={consented}
              required
              aria-invalid={fieldError ? true : undefined}
              onChange={(event) => {
                setConsented(event.target.checked);
                setFieldError(null);
              }}
            />
            {fieldError ? (
              <div className={styles.fieldError} role="alert" style={{ marginTop: 6 }}>
                {fieldError}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
